import React, { useEffect, useState } from "react";
import { Star, Users, CheckCircle2, Award } from "lucide-react";
import { toast } from "sonner";
import api, { fmtINR } from "@/lib/api";

export default function AgentShareTracker() {
  const [data, setData] = useState(null);

  const load = () => api.get("/admin/agent-share-tracker").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const queue = async (c) => {
    try {
      await api.post("/admin/agent-share/queue", { customer_id: c.customer_id });
      toast.success(`Agent Share queued for ${c.customer_name}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!data) return <div className="p-6 text-center text-slate-500">Loading...</div>;
  return (
    <div data-testid="agent-share-tracker-page">
      <div className="flex items-center gap-3">
        <Star className="w-7 h-7 text-violet-500" />
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">Special Agent Share</h1>
          <p className="text-sm text-slate-500">Eligible after <b>{data.threshold}</b> referrals complete yearly premium · <b>{data.share_percent}%</b> share of advisor commission</p>
        </div>
      </div>

      <div className="mt-5 bg-white border border-slate-100 rounded-2xl card-shadow table-scroll">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-bold">
            <tr>
              <th className="text-left p-3">Customer</th>
              <th className="text-center p-3">Total Referrals</th>
              <th className="text-center p-3">Completed (Yearly)</th>
              <th className="text-center p-3">Progress</th>
              <th className="text-right p-3">Suggested Share</th>
              <th className="text-center p-3">Status</th>
              <th className="text-right p-3">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.customers.map((c) => {
              const progress = Math.min(100, Math.round((c.completed_referrals / Math.max(1, c.threshold)) * 100));
              return (
                <tr key={c.customer_id} data-testid={`asr-row-${c.customer_id}`} className={c.eligible ? "bg-emerald-50/40 hover:bg-emerald-50" : "hover:bg-slate-50"}>
                  <td className="p-3">
                    <div className="font-bold">{c.customer_name}</div>
                    <div className="text-[10px] text-slate-400">+91 {c.phone}</div>
                  </td>
                  <td className="p-3 text-center font-bold">{c.total_referrals}</td>
                  <td className="p-3 text-center font-extrabold text-emerald-600">{c.completed_referrals}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full ${c.eligible ? "bg-emerald-500" : "bg-amber-400"}`} style={{ width: `${progress}%` }} />
                      </div>
                      <span className="text-[11px] font-bold text-slate-500 w-8 text-right">{progress}%</span>
                    </div>
                  </td>
                  <td className="p-3 text-right font-extrabold text-violet-700">{fmtINR(c.suggested_share)}</td>
                  <td className="p-3 text-center">
                    {c.eligible ? (
                      <span className="text-[10px] uppercase font-bold px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Eligible</span>
                    ) : (
                      <span className="text-[10px] uppercase font-bold px-2 py-1 rounded-full bg-slate-100 text-slate-500">{c.threshold - c.completed_referrals} to go</span>
                    )}
                  </td>
                  <td className="p-3 text-right">
                    {c.queued ? (
                      <span className={`text-[11px] font-bold ${c.queued_status === "approved" ? "text-emerald-600" : "text-amber-600"}`}>
                        {c.queued_status === "approved" ? "Credited ✓" : "Pending Approval"}
                      </span>
                    ) : c.eligible ? (
                      <button data-testid={`queue-share-${c.customer_id}`} onClick={() => queue(c)} className="text-[11px] bg-violet-600 text-white font-bold px-3 py-1.5 rounded-lg inline-flex items-center gap-1">
                        <Award className="w-3 h-3" /> Queue Share
                      </button>
                    ) : (
                      <span className="text-[11px] text-slate-300">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
            {data.customers.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-slate-400">No customers.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="text-[11px] text-slate-400 mt-3 flex items-center gap-1.5">
        <Users className="w-3.5 h-3.5" /> Queued share appears under <b>Benefit Approvals → Agent Share</b> for final approval & transfer.
      </div>
    </div>
  );
}
